/**
 * MessageAttachment Component
 * 
 * Displays file attachments in chat messages matching the Figma design.
 * Features file type icons, name display, and download functionality.
 * 
 * Features:
 * - File type icon display (PDF, images, documents)
 * - File name and size information
 * - Download/preview functionality
 * - Accessibility compliance
 * - Hover states and interactions
 */

import React from 'react';
import {
  Box,
  Text,
  Group,
  ActionIcon,
  Tooltip,
  rem,
  useMantineColorScheme,
} from '@mantine/core';
import {
  IconFile,
  IconFileTypePdf,
  IconPhoto,
  IconFileText,
  IconFileCode,
  IconDownload,
  IconEye,
} from '@tabler/icons-react';
import classes from './MessageAttachment.module.css';

// =============================================================================
// TYPES
// =============================================================================

export interface MessageAttachment {
  id: string;
  name: string;
  type: string;
  size: number;
  url?: string;
  preview?: string;
  uploadStatus?: 'pending' | 'uploading' | 'completed' | 'error';
}

export interface MessageAttachmentProps {
  attachment: MessageAttachment;
  onDownload?: (attachmentId: string) => void;
  onPreview?: (attachmentId: string) => void;
  className?: string;
  style?: React.CSSProperties;
}

// =============================================================================
// HELPERS
// =============================================================================

const getFileIcon = (fileType: string, fileName: string) => {
  const type = fileType.toLowerCase();
  const extension = fileName.split('.').pop()?.toLowerCase() || '';
  
  // PDF files
  if (type.includes('pdf') || extension === 'pdf') {
    return <IconFileTypePdf size={18} />;
  }
  
  // Image files
  if (type.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp'].includes(extension)) {
    return <IconPhoto size={18} />;
  }
  
  // Text/document files
  if (type.includes('text') || ['txt', 'doc', 'docx', 'rtf'].includes(extension)) {
    return <IconFileText size={18} />;
  }
  
  // Code files
  if (['js', 'ts', 'jsx', 'tsx', 'py', 'java', 'cpp', 'c', 'html', 'css', 'json'].includes(extension)) {
    return <IconFileCode size={18} />;
  }
  
  // Default file icon
  return <IconFile size={18} />;
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileTypeColor = (fileType: string, fileName: string): string => {
  const type = fileType.toLowerCase();
  const extension = fileName.split('.').pop()?.toLowerCase() || '';
  
  if (type.includes('pdf') || extension === 'pdf') return '#DC2626'; // Red for PDF
  if (type.includes('image')) return '#059669'; // Green for images
  if (type.includes('text') || ['txt', 'doc', 'docx'].includes(extension)) return '#2563EB'; // Blue for docs
  if (['js', 'ts', 'jsx', 'tsx', 'py', 'java'].includes(extension)) return '#7C3AED'; // Purple for code
  
  return '#6B7280'; // Gray for default
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export const MessageAttachment: React.FC<MessageAttachmentProps> = ({
  attachment,
  onDownload,
  onPreview,
  className,
  style,
}) => {
  // =============================================================================
  // EVENT HANDLERS
  // =============================================================================
  
  const handleDownload = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onDownload?.(attachment.id);
  };

  const handlePreview = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onPreview?.(attachment.id);
  };

  const handleAttachmentClick = () => {
    // Default action - try preview first, then download
    if (onPreview && attachment.url) {
      onPreview(attachment.id);
    } else if (onDownload && attachment.url) {
      onDownload(attachment.id);
    }
  };

  // =============================================================================
  // DERIVED STATE
  // =============================================================================
  
  const { colorScheme } = useMantineColorScheme();

  const fileIcon = getFileIcon(attachment.type, attachment.name);
  const fileSize = formatFileSize(attachment.size);
  const iconColor = colorScheme === 'dark'
    ? 'var(--fonterra-attachment-text)'
    : getFileTypeColor(attachment.type, attachment.name);
  const canPreview = attachment.preview || attachment.type.includes('image') || attachment.type.includes('pdf');

  // =============================================================================
  // RENDER
  // =============================================================================
  
  return (
    <Box 
      className={`${classes.container} ${className || ''}`}
      style={style}
      onClick={handleAttachmentClick}
    >
      <Group className={classes.content} gap="xs" wrap="nowrap">
        {/* File Icon */}
        <Box 
          className={classes.iconContainer}
          style={{ color: iconColor }}
        >
          {fileIcon}
        </Box>

        {/* File Info */}
        <Box className={classes.fileInfo} flex={1}>
          <Text className={classes.fileName} lineClamp={1}>
            {attachment.name}
          </Text>
          <Text className={classes.fileSize}>
            {fileSize}
          </Text>
        </Box>

        {/* Actions */}
        <Group className={classes.actions} gap="xs">
          {canPreview && onPreview && (
            <Tooltip label="Preview file" position="top">
              <ActionIcon
                className={classes.actionButton}
                onClick={handlePreview}
                aria-label="Preview file"
                size="sm"
                variant="subtle"
              >
                <IconEye size={16} />
              </ActionIcon>
            </Tooltip>
          )}
          
          {onDownload && (
            <Tooltip label="Download file" position="top">
              <ActionIcon
                className={classes.actionButton}
                onClick={handleDownload}
                aria-label="Download file"
                size="sm"
                variant="subtle"
              >
                <IconDownload size={16} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Group>
    </Box>
  );
};

// =============================================================================
// EXPORTS
// =============================================================================

export default MessageAttachment;
